import type { HTMLAttributes } from "react";

import { cx } from "@/lib/cx";

/**
 * A block of related content.
 *
 * This is where "remove the chrome" lands. Everything in the app used to be a
 * bordered *and* shadowed *and* rounded box — the tasks screen was a panel
 * containing a panel — and once every group looks like a card, no group looks
 * like anything. The rule from `index.css`:
 *
 * > structure comes from spacing and one hairline; borders only where two
 * > scroll regions meet; shadows only for things that float.
 *
 * Hence `bare` is the **default**: no border, no shadow, no background, just
 * a padded region on the page. Reach past it only for a stated reason:
 *
 * | tone       | when                                                          |
 * |------------|---------------------------------------------------------------|
 * | `bare`     | almost always — a section of a page                            |
 * | `hairline` | this block and the next one scroll independently               |
 * | `sunken`   | a well: a kanban lane, an inset list, a track                   |
 * | `raised`   | genuinely above the page: a peek body, a popover's interior     |
 * | `grouped`  | one card of an iOS grouped list — **only inside `bg-grouped`**  |
 * | `material` | chrome that floats over scrolling content and blurs it          |
 *
 * `flat` is kept as a spelling of `hairline` because it is written at a dozen
 * call sites. No tone carries a shadow; elevation belongs to something being
 * dragged or opened, not to a resting container.
 *
 * Every rounded tone also carries `squircle`, so the 12px surface corner
 * becomes continuous where `corner-shape` is supported and is unchanged
 * everywhere else. Nested controls use the separate 10px rung; buttons and
 * filters are pills.
 *
 * `grouped` is the one tone with a precondition: `bg-grouped-surface` is 1.17:1
 * against the ordinary page and **disappears** on it. It is a pair with
 * `bg-grouped`, which is what `ui/list`'s `GroupedList` paints. Use that
 * component unless you have a reason not to.
 */
export type PanelTone =
  | "bare"
  | "hairline"
  | "flat"
  | "sunken"
  | "raised"
  | "grouped"
  | "material";

/** `pad` is the ordinary 12px one. Legacy names stay on the compact grid. */
export type PanelPadding = "none" | "tight" | "pad" | "loose" | "sm" | "md" | "lg";

export type PanelProps = HTMLAttributes<HTMLDivElement> & {
  tone?: PanelTone;
  /** Internal padding. `none` lets the caller own it, e.g. for lists. */
  padding?: PanelPadding;
};

const TONES: Record<PanelTone, string> = {
  bare: "",
  // The hairline, not a 1px border: on a retina screen a 1px line is twice the
  // weight of the iOS separator and it is what made every block read as a card.
  // PS1: framed tones are the orange-framed dark box; `sunken` is a well.
  hairline: "rounded-surface border-2 border-ps1-orange bg-ps1-panel",
  flat: "rounded-surface border-2 border-ps1-orange bg-ps1-panel",
  sunken: "rounded-surface border border-ps1-line bg-sunken",
  raised: "rounded-surface border-2 border-ps1-orange bg-raised",
  grouped: "rounded-surface border-2 border-ps1-orange bg-grouped-surface",
  material: "rounded-surface border-2 border-ps1-orange bg-ps1-panel",
};

const PADDINGS: Record<PanelPadding, string> = {
  none: "",
  tight: "p-2",
  pad: "p-3",
  loose: "p-4",
  // Legacy spellings remain source-compatible without escaping the 16px cap.
  sm: "p-2",
  md: "p-3",
  lg: "p-4",
};

export function Panel({ tone = "bare", padding = "md", className, ...rest }: PanelProps) {
  return <div data-panel-tone={tone === "bare" ? undefined : tone} className={cx(TONES[tone], PADDINGS[padding], className)} {...rest} />;
}
